import React, { useRef, useState } from 'react';

const INITIAL_BALL = { x: 19, y: 76 };
const GOAL_BALL = { x: 50, y: 36 };

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function isGoal(point) {
  return point.x >= 28 && point.x <= 72 && point.y >= 17 && point.y <= 52;
}

export function PerifaNotFoundGame({ onReturn }) {
  const fieldRef = useRef(null);
  const [ball, setBall] = useState(INITIAL_BALL);
  const [dragging, setDragging] = useState(false);
  const [scored, setScored] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [message, setMessage] = useState('404 fora de campo. Acerte o gol para seguir no Perifa Pro.');

  const pointFromEvent = (event) => {
    const rect = fieldRef.current?.getBoundingClientRect();
    if (!rect) {
      return ball;
    }

    return {
      x: clamp(((event.clientX - rect.left) / rect.width) * 100, 7, 93),
      y: clamp(((event.clientY - rect.top) / rect.height) * 100, 12, 86),
    };
  };

  const finishShot = (point) => {
    setAttempts((current) => current + 1);
    if (isGoal(point)) {
      setBall(GOAL_BALL);
      setScored(true);
      setMessage('GOL! Você é um grande jogador por fazer parte do Perifa Pro.');
      return;
    }

    setBall(point);
    setScored(false);
    setMessage('Quase. Ajuste a mira e chute de novo enquanto a página volta ao normal.');
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
    setScored(false);
    setBall(pointFromEvent(event));
  };

  const handlePointerMove = (event) => {
    if (!dragging) {
      return;
    }
    setBall(pointFromEvent(event));
  };

  const handlePointerUp = (event) => {
    if (!dragging) {
      return;
    }
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setDragging(false);
    finishShot(pointFromEvent(event));
  };

  const scoreGoal = () => {
    finishShot(GOAL_BALL);
  };

  const resetShot = () => {
    setBall(INITIAL_BALL);
    setScored(false);
    setMessage('404 fora de campo. Acerte o gol para seguir no Perifa Pro.');
  };

  return (
    <section className={`not-found-game ${scored ? 'has-scored' : ''}`}>
      <div className="not-found-copy">
        <span className="eyebrow">Perifa Pro</span>
        <h1>404 fora de campo</h1>
        <p>
          A rota saiu da jogada. Enquanto a página volta ao normal, tente mandar a bola para a rede.
        </p>
      </div>

      <div
        className="not-found-field"
        ref={fieldRef}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div className="not-found-goal" aria-hidden="true">
          <div className="not-found-goal__net">
            <strong>404</strong>
          </div>
        </div>
        <div className="not-found-penalty-line" aria-hidden="true" />
        {scored ? (
          <div className="not-found-goal-burst" aria-hidden="true">
            {Array.from({ length: 14 }).map((_, index) => (
              <span key={index} style={{ '--burst-index': index }} />
            ))}
          </div>
        ) : null}
        <button
          type="button"
          className={`not-found-ball ${dragging ? 'is-dragging' : ''} ${scored ? 'is-goal' : ''}`}
          style={{ '--ball-x': `${ball.x}%`, '--ball-y': `${ball.y}%` }}
          onPointerDown={handlePointerDown}
          aria-label="Bola do desafio 404"
        >
          <span aria-hidden="true" />
        </button>
      </div>

      <div className="not-found-status" aria-live="polite">
        <strong>{scored ? 'GOL!' : 'Desafio 404'}</strong>
        <p>{message}</p>
        <span>{attempts ? `${attempts} chute${attempts > 1 ? 's' : ''}` : 'Primeiro chute pronto'}</span>
      </div>

      <div className="not-found-actions">
        <button className="primary-button" type="button" onClick={scoreGoal}>
          Chutar no gol
        </button>
        <button className="ghost-button" type="button" onClick={resetShot}>
          Continuar jogando
        </button>
        <button className="inline-button" type="button" onClick={onReturn}>
          Voltar para o Perifa Pro
        </button>
      </div>
    </section>
  );
}
